import { displayFromCodeableConcept } from '@seen/core/codeableConcept'
import { invariant } from '@seen/core/invariant'
import { Incident } from '@seen/core/models/assessments/incident'
import { IncidentTemplate } from '@seen/core/models/incidentTemplate'
import { Button, DotSeparator, TypographyPageTitle } from '@seen/ui'
import { IconPlus } from '@tabler/icons-react'
import type { ColumnDef } from '@tanstack/react-table'
import { orderBy } from 'lodash-es'
import type { JSX } from 'react'
import { useNavigate } from 'react-router'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { HoverPractitioner } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { TemplateCombobox } from '#src/components/TemplateCombobox.tsx'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useNavigateFromClickEvent } from '#src/hooks/useNavigateFromClickEvent.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { extractTextFromHTML, extractTitleFromHTML } from '#src/utils/html.ts'

export function IncidentsPageComponent(): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const navigate = useNavigate()
  const navigateFromClick = useNavigateFromClickEvent()
  const practitioner = useCurrentPractitioner()
  const participant = useCurrentParticipant()
  const { data: incidents } = useSearch(
    Incident,
    Incident.searchQuery({
      subject: participant.reference,
    })
  )

  const createIncident = useCreateMutation<Incident>()
  const mutate = usePatchMutation<Incident>().mutate

  if (!incidents) {
    return <Loader />
  }

  const tableData = orderBy(
    incidents,
    ['signedOn', 'startsAt'],
    ['desc', 'desc']
  )

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <TypographyPageTitle>Incidents</TypographyPageTitle>
        {!isReadOnly && (
          <TemplateCombobox
            templateClass={IncidentTemplate}
            onSelect={(template) => {
              createIncident.mutate(
                Incident.buildFromTemplate(
                  practitioner.reference,
                  participant.reference,
                  template
                ),
                {
                  onSuccess: (createdIncident) => {
                    void navigate(`./${createdIncident.id}`)
                  },
                }
              )
            }}
          >
            <Button variant="outline" className="flex items-center gap-2">
              <IconPlus className="size-4" />
              New incident…
            </Button>
          </TemplateCombobox>
        )}
      </div>
      <DataTable
        columns={columns}
        data={tableData}
        onRowClick={(row, e) => navigateFromClick(`./${row.id}`, e)}
        deleteRowProps={(row) => ({
          title: `Delete "${extractTitleFromHTML(row.title)}"?`,
          description:
            'Deleted incidents can still be recovered by contacting our team.',
          onConfirm: () => {
            const incident = incidents.find((i) => i.id === row.id)
            invariant(incident, 'Incident is required')
            mutate({
              instance: incident,
              mutateFn: (i) => {
                i.status = 'entered-in-error'
              },
            })
          },
        })}
      />
    </div>
  )
}

const columns: ColumnDef<Incident>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <>
        {extractTextFromHTML(row.original.title)}
        <DotSeparator />
        <span className="text-foreground-secondary">
          {displayFromCodeableConcept(row.original.type)}
        </span>
      </>
    ),
  },
  {
    accessorKey: 'createdBy',
    header: 'Created by',
    cell: ({ row }) => <HoverPractitioner practitioner={row.original.author} />,
  },
  {
    accessorKey: 'signedBy',
    header: 'Signed by',
    cell: ({ row }) => {
      const signer = row.original.signer
      return signer ? <HoverPractitioner practitioner={signer} /> : '--'
    },
  },
  {
    accessorKey: 'created',
    header: 'Created',
    cell: ({ row }) =>
      row.original.startsAt ? <HoverDate date={row.original.startsAt} /> : null,
  },
  {
    accessorKey: 'signedOn',
    header: 'Signed',
    cell: ({ row }) =>
      row.original.signedOn ? <HoverDate date={row.original.signedOn} /> : null,
  },
]
